import { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Button,
    CircularProgress,
    Typography,
    Slider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate } from "react-router-dom";

const ImageBrowserPopup = ({ open, onClose, onImageSelect }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const imageType = localStorage.getItem("imageType");
    const Id = localStorage.getItem("userId")
    const [AccessToken, setAccessToken] = useState()
    useEffect(() => {
        const storedAccessToken = localStorage.getItem("AccessToken");
        setAccessToken(storedAccessToken)
    }, []);
    useEffect(() => {
        if (open) {
            fetchImages();
        } else {

            setImages([])
            setSelectedImage(null);
            setZoomLevel(1);
        }  // eslint-disable-next-line
    }, [open]);

    const fetchImages = async () => {
        setLoading(true);
        try {
            let response;
            if (imageType === "model") {
                response = await fetch(`${process.env.REACT_APP_API_KEY}/images/get_images?user_id=0&image_type=model`);
            }
            if (imageType === "captured") {
                response = await fetch(`${process.env.REACT_APP_API_KEY}/images/get_images?user_id=${Id}&image_type=captured`);
            }
            if (imageType === "uploaded") {
                response = await fetch(`${process.env.REACT_APP_API_KEY}/images/get_images?user_id=${Id}&image_type=uploaded`);
            }
            if (!response.ok) {
                throw new Error("Failed to fetch images from API");
            }
            const data = await response.json();
            // Assume each image object has an 'id' and 'image' (Base64 encoded string)
            const transformedImages = data.map((img) => ({
                id: img.id,
                url: `data:image/jpeg;base64,${img.image}`
            }));
            setImages(transformedImages);

        } catch (error) {
            console.error("Error fetching images:", error);
        } finally {
            setLoading(false);
        } //react - hooks / exhaustive - deps
    };

    const handleImageClick = (image) => {
        console.log("Selected image:", image);
        setSelectedImage(image);
    };

    const handleZoomIn = () => {
        setZoomLevel((prev) => Math.min(prev + 0.1, 2)); // Max zoom level: 2x
    };

    const handleZoomOut = () => {
        setZoomLevel((prev) => Math.max(prev - 0.1, 1)); // Min zoom level: 1x
    };

    const handleZoomChange = (event, newValue) => {
        setZoomLevel(newValue);
    };

    const handleSelectImage = () => {
        if (selectedImage) {
            onImageSelect(selectedImage);
            onClose();
        }
    };

    const handleCaptureImage = () => {
        onClose();
        const Id = localStorage.getItem("userId")
        if (!Id) {
            navigate(`/${AccessToken}/login`)
            return
        }
        navigate(`/${AccessToken}/capture`);
    };

    // Handle file upload button click
    const handleUploadImage = () => {
        const Id = localStorage.getItem("userId")
        if (!Id) {
            navigate(`/${AccessToken}/login`)
            return
        }
        // Trigger the hidden file input
        fileInputRef.current.click();
    };

    // Handle file selection
    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Only process image files
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        setUploading(true);
        try {
            // Create a FormData object to send the file
            const formData = new FormData();

            formData.append("file", file, "capture.jpg");
            formData.append("image_type", imageType);
            formData.append("user_id", Id);

            // Send the file to your API
            const response = await fetch(`${process.env.REACT_APP_API_KEY}/images/upload_image_to_DB`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Upload failed: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('Upload successful:', result);

            // Refresh the image list
            fetchImages();
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image. Please try again.');
        } finally {
            setUploading(false);
            // Reset the file input
            event.target.value = '';
        }
    };

    // Handle delete icon click
    const handleDeleteClick = (image, event) => {
        event.stopPropagation(); // Prevent image selection
        setImageToDelete(image); // Set the image to delete
        setDeleteConfirmationOpen(true); // Open confirmation dialog
    };

    // Handle confirmation dialog close
    const handleDeleteConfirmationClose = () => {
        setDeleteConfirmationOpen(false);
        setImageToDelete(null);
    };

    // Handle confirmed delete action
    const handleDeleteConfirmed = async () => {
        if (imageToDelete) {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_KEY}/images/deleteImage?imageId=${imageToDelete.id}`,
                    {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(`Failed to delete image: ${response.statusText}`);
                }

                // Remove the image from the local state
                setImages((prev) => prev.filter((img) => img.id !== imageToDelete.id));
            } catch (error) {
                console.error("Error deleting image:", error);
            } finally {
                setDeleteConfirmationOpen(false);
                setImageToDelete(null);
            }
        }
    };

    // Calculate block size based on zoom level
    const getBlockSize = () => {
        const baseSize = 150; // Base size for the block
        return baseSize * zoomLevel; // Scale the block size based on zoom level
    };

    return (
        <>
            {/* Hidden file input */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleFileChange}
            />

            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: "rgba(13, 17, 23, 0.95)",
                        backdropFilter: "blur(12px)",
                        boxShadow: "0px 4px 30px rgba(0, 0, 0, 0.3)",
                        color: "#fff",
                    },
                }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6" sx={{ color: "#fff", fontWeight: "bold" }}>
                        Images
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ borderColor: "rgba(255, 255, 255, 0.1)" }}>
                    {loading || uploading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                            <CircularProgress sx={{ color: "#ff4081" }} />
                            {uploading && (
                                <Typography sx={{ ml: 2, color: "#ccc" }}>
                                    Uploading image...
                                </Typography>
                            )}
                        </Box>
                    ) : images.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                            <Typography sx={{ color: "#ccc" }}>
                                {imageType === "uploaded" ? "No uploaded images found" : "No captured images found"}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    mb: 2,
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <IconButton onClick={handleZoomOut} sx={{ color: "#fff", padding: "4px" }}>
                                    <ZoomOutIcon />
                                </IconButton>
                                <Slider
                                    size="small"
                                    value={zoomLevel}
                                    min={1.1}
                                    max={2}
                                    step={0.1}
                                    onChange={handleZoomChange}
                                    sx={{
                                        width: 100,
                                        color: "#ff4081",
                                        "& .MuiSlider-thumb": {
                                            width: 14,
                                            height: 14,
                                        },
                                    }}
                                />
                                <IconButton onClick={handleZoomIn} sx={{ color: "#fff", padding: "4px" }}>
                                    <ZoomInIcon />
                                </IconButton>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 2,
                                    justifyContent: "flex-start",
                                }}
                            >
                                {images.map((image) => (
                                    <Box
                                        key={image.id}
                                        onClick={() => handleImageClick(image)}
                                        sx={{
                                            width: getBlockSize(),
                                            height: getBlockSize() * 1.33, // Maintain 3:4 aspect ratio
                                            border: selectedImage?.id === image.id
                                                ? "3px solid #ff4081"
                                                : "1px solid rgba(255, 255, 255, 0.2)",
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            position: "relative",
                                            "&:hover": {
                                                transform: "scale(1.02)",
                                                boxShadow: "0px 0px 20px rgba(255, 64, 129, 0.3)",
                                            },
                                            "&:hover .delete-icon": {
                                                display: "block",
                                            },
                                        }}
                                    >
                                        {imageType !== "model" && (
                                            <IconButton
                                                className="delete-icon"
                                                onClick={(e) => handleDeleteClick(image, e)}
                                                sx={{
                                                    position: "absolute",
                                                    top: 10,
                                                    right: 10,
                                                    color: "#fff",
                                                    background: "rgba(255, 0, 0, 0.7)",
                                                    width: 40, // Set a fixed width
                                                    height: 40, // Set the same fixed height
                                                    padding: 0, // Remove padding to ensure proper circle shape
                                                    minWidth: 0, // Remove minimum width constraint
                                                    borderRadius: "50%", // Ensure perfect circle
                                                    "&:hover": {
                                                        background: "rgba(255, 0, 0, 0.9)",
                                                    },
                                                    display: "none",
                                                }}
                                            >
                                                <DeleteIcon fontSize="medium" />
                                            </IconButton>
                                        )}
                                        {selectedImage?.id === image.id && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: "rgba(255, 64, 129, 0.1)",
                                                    display: "flex",
                                                    alignItems: "flex-end",
                                                    justifyContent: "flex-start",
                                                    zIndex: 2,
                                                    padding: 1,
                                                }}
                                            >
                                                <CheckCircleIcon
                                                    sx={{
                                                        color: "rgb(14, 55, 179)",
                                                        fontSize: 28,
                                                    }}
                                                />
                                            </Box>
                                        )}

                                        <img
                                            // eslint-disable-next-line
                                            src={image.url}
                                            alt={`Image_${image.id}`}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                            onError={(e) => {
                                                console.error(`Failed to load image: ${image.url}`);
                                                e.target.src = "https://placehold.co/300x400/FF4081/FFFFFF/png?text=Error";
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2,
                        borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                >
                    {imageType === "captured" && (
                        <Button
                            variant="contained"
                            startIcon={<CameraAltIcon />}
                            onClick={handleCaptureImage}
                            sx={{
                                background: "#ff4081",
                                color: "#fff",
                                textTransform: "none",
                                borderRadius: "8px",
                                marginRight: "auto",
                                padding: "10px 20px",
                                "&:hover": {
                                    background: "#f50057",
                                },
                            }}
                        >
                            Capture New Image
                        </Button>
                    )}

                    {imageType === "uploaded" && (
                        <Button
                            variant="contained"
                            startIcon={<CloudUploadIcon />}
                            onClick={handleUploadImage}
                            disabled={uploading}
                            sx={{
                                background: "#2196f3",
                                color: "#fff",
                                textTransform: "none",
                                borderRadius: "8px",
                                marginRight: "auto",
                                padding: "10px 20px",
                                "&:hover": {
                                    background: "#1976d2",
                                },
                            }}
                        >
                            {uploading ? "Uploading..." : "Upload New Image"}
                        </Button>
                    )}

                    <Box sx={{ ml: "auto" }}>
                        <Button
                            onClick={onClose}
                            sx={{
                                color: "#fff",
                                textTransform: "none",
                                mr: 1,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSelectImage}
                            disabled={!selectedImage}
                            sx={{
                                background: "#ff4081",
                                color: "#fff",
                                textTransform: "none",
                                borderRadius: "8px",
                                padding: "10px 20px",
                                "&:hover": {
                                    background: "#f50057",
                                },
                                "&.Mui-disabled": {
                                    background: "rgba(255, 64, 129, 0.3)",
                                    color: "rgba(255, 255, 255, 0.5)",
                                },
                            }}
                        >
                            Open
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteConfirmationOpen}
                onClose={handleDeleteConfirmationClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this image?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteConfirmationClose}>Cancel</Button>
                    <Button onClick={handleDeleteConfirmed} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ImageBrowserPopup;